import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import './technical-indicators.ui.scss';
import { TechnicalIndicatorDto, TypeTechnicalIndicator } from '../../../services/dtos/technical-indicator.dto';
import { getColorChart, getLabelChart } from './technical-indicators-data.ui';
import { BarChart, LineChart } from '@mui/x-charts';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EuroIcon from '@mui/icons-material/Euro';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import theme from '../../../theme';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import logoRegie from '../../../assets/img/LogoRegie.png';
import { StyledTableRow } from '../../commun/table-row';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import { IndicatorValuesDto, IndicatorValuesFacturesDto } from '../../../services/dtos/indicator-values.dto';

/**
 * Vue pour L'affichage de l'onglet indicateurs techniques
 * @param props props de l'ihm
 * @constructor
 */
export default function TechnicalIndicatorsPdfUi(
    props: Readonly<{
        chartValues: TechnicalIndicatorDto;
        subtitle?: string | null;
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        headCells:
            | HeadCellDto<IndicatorValuesDto, keyof IndicatorValuesDto>[]
            | HeadCellDto<IndicatorValuesFacturesDto, keyof IndicatorValuesFacturesDto>[];
    }>
) {
    //Retourne la vue a afficher suivant s'il y a des valeurs ou non
    return props.chartValues.values !== undefined ? (
        <Grid container>
            <Grid item xs={3} />
            <Grid item xs={6}>
                <div id={'indicator-' + props.chartValues.id}>
                    <Grid container className={'technical-indicators-pdf'}>
                        {/*Bande bleu avec titre*/}
                        <Grid item xs={12} sx={{ marginBottom: '10px' }}>
                            <Box
                                sx={{
                                    backgroundColor: '#3766A5',
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '20px',
                                        color: 'white !important',
                                    }}
                                >
                                    {getLabel(props.chartValues.type)}
                                </Typography>
                            </Box>
                        </Grid>
                        {/*Logo*/}
                        <Grid item xs={1} sm={2}>
                            <Typography component="img" src={logoRegie} sx={{ width: 80, paddingLeft: 5 }}></Typography>
                        </Grid>
                        {/*sous titre*/}
                        <Grid item xs={10} sm={8}>
                            <Typography
                                variant="h5"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '30px',
                                    color: theme.palette.blue.main,
                                    textAlign: 'center',
                                }}
                            >
                                <span className="bold">{props.subtitle}</span>
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sm={2} />
                        <Grid item xs={1} sm={2} />
                        <Grid item xs={10} sm={8}>
                            {/*Titre du graphique*/}
                            <Typography
                                variant="body1"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '20px',
                                    color: theme.palette.blue.main,
                                }}
                            >
                                {getIconIndicators(props.chartValues.type)}
                                <span className="bold small-margin-left">
                                    {props.chartValues.currentValue} {props.chartValues.unit}
                                </span>
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sm={2} />
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                            {/*Si c'est le comparateur des factures théoriques/réelles on affiche un chart bar avec deux données*/}
                            {props.chartValues.type === TypeTechnicalIndicator.COMPARETHEORETICALREAL && (
                                <BarChart
                                    skipAnimation
                                    width={750}
                                    height={450}
                                    xAxis={[
                                        {
                                            data: props.chartValues.values.map(currentValue => currentValue.label),
                                            scaleType: 'band',
                                            label: 'Date',
                                        },
                                    ]}
                                    series={[
                                        {
                                            data: props.chartValues.values.map(result =>
                                                Number.parseFloat(result.reel)
                                            ),
                                            label: getLabelChart(props.chartValues.type),
                                            color: getColorChart(props.chartValues.type),
                                        },
                                        {
                                            data: props.chartValues.values.map(result =>
                                                Number.parseFloat(result.theorique)
                                            ),
                                            label: getLabelChart(props.chartValues.type, 'theorique'),
                                            color: getColorChart(props.chartValues.type, 'theorique'),
                                        },
                                    ]}
                                />
                            )}
                            {/*Si c'est le comparateur des recettes on affiche un chart bar avec une données*/}
                            {props.chartValues.type === TypeTechnicalIndicator.RECIPEEXCLUDINGTAX && (
                                <BarChart
                                    skipAnimation
                                    width={750}
                                    height={450}
                                    xAxis={[
                                        {
                                            data: props.chartValues.values.map(currentValue => currentValue.label),
                                            scaleType: 'band',
                                            label: 'Date',
                                        },
                                    ]}
                                    series={[
                                        {
                                            data: props.chartValues.values.map(currentValue =>
                                                Number.parseFloat(currentValue.value)
                                            ),
                                            label: getLabelChart(props.chartValues.type),
                                            color: getColorChart(props.chartValues.type),
                                        },
                                    ]}
                                />
                            )}
                            {/*Si c'est un indicateur autre que les deux premiers, on affiche un chart avec des lines*/}
                            {props.chartValues.type !== TypeTechnicalIndicator.RECIPEEXCLUDINGTAX &&
                                props.chartValues.type !== TypeTechnicalIndicator.COMPARETHEORETICALREAL && (
                                    <LineChart
                                        width={750}
                                        height={450}
                                        xAxis={[
                                            {
                                                data: props.chartValues.values.map(currentValue => currentValue.label),
                                                scaleType: 'point',
                                                label: 'Date',
                                            },
                                        ]}
                                        series={[
                                            {
                                                data: props.chartValues.values.map(currentValue =>
                                                    Number.parseFloat(currentValue.value)
                                                ),
                                                label:
                                                    getLabelChart(props.chartValues.type) +
                                                    ` (en ${props.chartValues.unit})`,
                                                color: getColorChart(props.chartValues.type),
                                            },
                                        ]}
                                    />
                                )}
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
                            <TableContainer sx={{ width: '750px' }}>
                                <Table>
                                    {/*// Affichage du tableau*/}
                                    <TableHead sx={{ borderBottom: '2px solid #000 !important' }}>
                                        <TableRow>
                                            {/*En-têtes*/}
                                            {props.headCells.map(headCell => (
                                                <TableCell
                                                    key={`headCell-${headCell.id.toString()}`}
                                                    align={'left'}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: 'medium',
                                                        padding: '5px',
                                                        color: '#524c41',
                                                        width: headCell.width ? headCell.width + '%' : 100,
                                                    }}
                                                >
                                                    {headCell.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        <>
                                            {/*// Lignes du tableau*/}
                                            {props.chartValues.values?.map(row => (
                                                // Ajout d'une row stylisée
                                                <StyledTableRow key={`row-${row.label}-${row.key}`}>
                                                    {/*En-têtes*/}
                                                    {props.headCells.map(headCell => (
                                                        // La key id du headcell doit permettre d'avoir l'information de la row
                                                        <TableCell
                                                            key={`rowCell-${headCell.id.toString()}`}
                                                            sx={{ padding: '5px', color: '#524c41' }}
                                                        >
                                                            <>{row[headCell.id]}</>
                                                        </TableCell>
                                                    ))}
                                                </StyledTableRow>
                                            ))}
                                        </>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </div>
            </Grid>
            <Grid item xs={3} />
        </Grid>
    ) : (
        <></>
    );
}

/**
 * Méthode pour les icones du PDF
 * @param type
 */
export function getIconIndicators(type: TypeTechnicalIndicator) {
    switch (type) {
        //Comparaison entre le théorique facturé et le réel
        //Volume comptabilisé
        //Volume produit
        case TypeTechnicalIndicator.PRODUCTVOLUME:
        case TypeTechnicalIndicator.VOLUMECOUNTED:
        case TypeTechnicalIndicator.COMPARETHEORETICALREAL:
            return (
                <>
                    <WaterDropIcon sx={{ marginRight: '10px', fontSize: '2em' }} />
                </>
            );
        //Rendement
        case TypeTechnicalIndicator.EFFICIENCY:
            return (
                <>
                    <AccountBalanceIcon sx={{ marginRight: '10px', fontSize: '2em' }} />
                </>
            );
        //Evolution du facturé
        case TypeTechnicalIndicator.EVOLUTIONBILLED:
            return (
                <>
                    <EuroIcon sx={{ marginRight: '10px', fontSize: '2em' }} />
                </>
            );
        //Linéaire de réseau
        case TypeTechnicalIndicator.LINEARNETWORK:
            return (
                <>
                    <OpenInFullIcon sx={{ transform: 'rotate(90deg)', marginRight: '10px', ffontSize: '2em' }} />
                </>
            );
        //Recette HT - redevance comprise (par exercice comptable)
        case TypeTechnicalIndicator.RECIPEEXCLUDINGTAX:
            return (
                <>
                    <SignalCellularAltIcon sx={{ marginRight: '10px', fontSize: '2em' }} />
                </>
            );
        default:
            return '';
    }
}

/**
 * Méthode pour le libellé du PDF
 * @param type
 */
export function getLabel(type: TypeTechnicalIndicator) {
    switch (type) {
        //Comparaison entre le théorique facturé et le réel
        case TypeTechnicalIndicator.COMPARETHEORETICALREAL:
            return 'Comparaison entre le théorique facturé et le réel';
        //Rendement
        case TypeTechnicalIndicator.EFFICIENCY:
            return 'Rendement';
        //Evolution du facturé
        case TypeTechnicalIndicator.EVOLUTIONBILLED:
            return 'Evolution du facturé';
        //Linéaire de réseau
        case TypeTechnicalIndicator.LINEARNETWORK:
            return 'Linéaire de réseau';
        //Volume produit
        case TypeTechnicalIndicator.PRODUCTVOLUME:
            return 'Volume produit';
        //Recette HT - redevance comprise (par exercice comptable)
        case TypeTechnicalIndicator.RECIPEEXCLUDINGTAX:
            return 'Recette HT - redevance comprise (par exercice comptable)';
        //Volume comptabilisé
        case TypeTechnicalIndicator.VOLUMECOUNTED:
            return 'Volume comptabilisé';
        default:
            return '';
    }
}
